// src/prototype/components/oir/report/OIRReportIndex.tsx
// ============================================================
// Sticky left rail navigation for OIRReportView
// - Smooth scroll to section anchors
// - IntersectionObserver active section highlight
// - Mobile: collapses to "Jump to section" dropdown
// ============================================================

import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

// ── Section definitions ────────────────────────────────────────────────────
// Order updated March 2026: Financial Impact moved above Strategy Decision Canvas
// to support executive cognition: value first, then selection.
export const REPORT_SECTIONS = [
  { id: 'oir-exec-summary',              label: 'Executive Summary' },
  { id: 'oir-strategic-context',         label: 'Strategic Context' },
  { id: 'oir-opportunity-landscape',     label: 'Opportunity Landscape' },
  { id: 'oir-financial-impact',          label: 'Financial Impact' },
  { id: 'oir-strategy-decision-canvas',  label: 'Strategy Decision Canvas' },
  { id: 'oir-execution-roadmap',         label: 'Execution Roadmap' },
] as const;

type SectionId = typeof REPORT_SECTIONS[number]['id'];

// ── Smooth scroll helper ───────────────────────────────────────────────────
const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;
  const offset = 80; // account for sticky header
  const top = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: 'smooth' });
};

// ── Component ──────────────────────────────────────────────────────────────
export const OIRReportIndex: React.FC = () => {
  const [activeId, setActiveId] = useState<SectionId>(REPORT_SECTIONS[0].id);
  const [mobileOpen, setMobileOpen] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    // Map to track visible ratio per section
    const visibleMap: Record<string, number> = {};

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          visibleMap[entry.target.id] = entry.intersectionRatio;
        });
        // Pick the section with highest intersection ratio
        let bestId = activeId;
        let bestRatio = -1;
        for (const [id, ratio] of Object.entries(visibleMap)) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            bestId = id as SectionId;
          }
        }
        if (bestRatio > 0) setActiveId(bestId as SectionId);
      },
      {
        rootMargin: '-80px 0px -40% 0px',
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0],
      }
    );

    REPORT_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observerRef.current?.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activeLabel = REPORT_SECTIONS.find(s => s.id === activeId)?.label ?? '';

  return (
    <>
      {/* ── Desktop: sticky left rail ──────────────────────────────────── */}
      <nav className="hidden md:flex flex-col w-[240px] flex-shrink-0 sticky top-20 self-start h-fit">
        {/* Index header */}
        <p className="text-[10px] font-semibold text-[#18365E]/40 uppercase tracking-widest mb-3 px-1">
          Report Index
        </p>

        <ol className="flex flex-col gap-0.5">
          {REPORT_SECTIONS.map((section, idx) => {
            const isActive = activeId === section.id;
            return (
              <li key={section.id}>
                <button
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs transition-all group ${
                    isActive
                      ? 'bg-[#18365E]/[0.07] text-[#18365E] font-semibold'
                      : 'text-[#717182] hover:text-[#18365E] hover:bg-[#18365E]/[0.04] font-medium'
                  }`}
                >
                  {/* Step number */}
                  <span
                    className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${
                      isActive
                        ? 'bg-[#18365E] text-white'
                        : 'bg-[#18365E]/10 text-[#18365E]/50 group-hover:bg-[#18365E]/20 group-hover:text-[#18365E]/70'
                    }`}
                  >
                    {idx + 1}
                  </span>
                  {section.label}
                </button>
              </li>
            );
          })}
        </ol>

        {/* Divider + branding */}
        <div className="mt-5 pt-4 border-t border-[#e5e7eb]">
          <p className="text-[9px] text-[#717182]/60 px-1 leading-relaxed">
            Portfolio Strategy Report
            <br />
            <span className="text-[#FF6E4C]/70">DVA · Adaptive Product</span>
          </p>
        </div>
      </nav>

      {/* ── Mobile: dropdown ───────────────────────────────────────────── */}
      <div className="md:hidden mb-3">
        <button
          onClick={() => setMobileOpen(v => !v)}
          className="flex items-center justify-between w-full px-3 py-2 bg-white border border-[#e5e7eb] rounded-lg text-xs font-semibold text-[#18365E] shadow-sm"
        >
          <span>Jump to: {activeLabel}</span>
          <ChevronDown
            className={`w-3.5 h-3.5 text-[#18365E]/50 transition-transform ${mobileOpen ? 'rotate-180' : ''}`}
          />
        </button>
        {mobileOpen && (
          <div className="mt-1 bg-white border border-[#e5e7eb] rounded-lg shadow-md overflow-hidden z-30">
            {REPORT_SECTIONS.map((section, idx) => (
              <button
                key={section.id}
                onClick={() => {
                  scrollToSection(section.id);
                  setMobileOpen(false);
                }}
                className="w-full text-left px-4 py-2.5 text-xs text-[#18365E] hover:bg-[#18365E]/[0.05] flex items-center gap-2 border-b border-[#e5e7eb] last:border-0"
              >
                <span className="w-4 h-4 rounded-full bg-[#18365E]/10 text-[#18365E]/60 text-[10px] font-bold flex items-center justify-center flex-shrink-0">
                  {idx + 1}
                </span>
                {section.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default OIRReportIndex;